<!-- eslint-disable @typescript-eslint/no-magic-numbers -->
<script lang="ts" setup>
    import { type TemplateTyping } from '@/typings/widget';
    import { type IriTyping } from '@shared/typings';
    import TemplateItem from './TemplateItem.ce.vue';

    const props = defineProps<{
        modelValue: IriTyping
        templates: TemplateTyping[]
    }>();

    // eslint-disable-next-line func-call-spacing
    const emit = defineEmits<{
        (event: 'update:modelValue', value: IriTyping): void
        (event: 'next'): void
    }>();

    const { t } = useI18n();

    const selectedTemplate: Ref<IriTyping> = ref(props.modelValue ?? '');

    const handleNextStep = async(template: TemplateTyping): Promise<void> => {
        selectedTemplate.value = template.id;

        await nextTick(() => {
            emit('next');
        });
    };

    watch(() => selectedTemplate.value, (nVal: IriTyping) => {
        emit('update:modelValue', nVal);
    });
</script>

<template>
    <div class="template-selection">
        <div class="template-selection__content">
            <div class="template-selection__intro">
                <span class="template-selection__title">
                    {{ t('widget.quote.template.title') }}
                </span>
                <span class="template-selection__subtitle">
                    {{ t('widget.quote.template.subtitle') }}
                </span>
            </div>
            <div class="template-selection__list">
                <TemplateItem
                    v-for="template in templates"
                    :key="template.id"
                    :template="template"
                    :selected="selectedTemplate === template.id"
                    @click="handleNextStep(template)"
                />
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .template-selection {
        display: flex;
        justify-content: center;
        block-size: 100%;
        inline-size: 100%;

        &__content {
            display: flex;
            flex-direction: column;
            gap: var(--size-7);
            inline-size: 100%;
            padding-block: var( --spacing-07);
            padding-inline: calc(var(--spacing-04) + var(--spacing-01));

            @include min-width(md) {
                padding-block-start: var(--spacing-09);
            }

            @include min-width(lg) {
                padding-block: var(--spacing-01);
            }
        }

        &__intro {
            display: flex;
            flex-direction: column;
            justify-content: center;

            @include min-width(md) {
                gap: var(--spacing-01);
            }

            @include min-width(lg) {
                gap: var(--spacing-02);
            }
        }

        &__title {
            color: var(--text-primary);
            font-family: var(--font-primary);
            font-size: var(--font-h4);
            font-weight: var(--font-weight-medium);
            line-height: var(--line-height-150);
            padding-inline: var(--spacing-05);
            text-align: center;
        }

        &__subtitle {
            text-align: center;
        }

        &__list {
            display: grid;
            gap: var(--spacing-02);
            grid-template-columns: 100%;
            padding-block-end: var(--spacing-07);

            @include min-width(md) {
                grid-template-columns: repeat(2, 50%);
            }

            @include min-width(lg) {
                grid-template-columns: repeat(3, 33.333%);
                padding-inline: var(--spacing-12);
            }
        }
    }
</style>
